import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import type {PagePromptContent} from '../store/thread';
import {
  type AgentPageContextChannel,
  type AgentPageContextLayerNode,
  registerAgentPageContextFact,
} from './channel';

const LayerContext = createContext<AgentPageContextLayerNode | null>(null);
const GroupContext = createContext<GroupScope | null>(null);

interface GroupScope {
  groupKey: string;
  order: number;
}

/**
 * AgentPageContextLayer marks a page-context layer of the given type. Facts
 * published beneath it, and not beneath a nested layer, belong to it. It
 * clears any inherited group so a layer always starts a fresh grouping.
 */
export const AgentPageContextLayer = ({
  type,
  children,
}: {
  type: PagePromptContent['type'];
  children?: ReactNode;
}) => {
  const parent = useContext(LayerContext);
  const node = useStableLayer(type, parent);

  return (
    <LayerContext.Provider value={node}>
      <GroupContext.Provider value={null}>{children}</GroupContext.Provider>
    </LayerContext.Provider>
  );
};

/**
 * AgentPageContextGroup buckets the facts beneath it under `groupKey` so a
 * layer reducer can read them as one instance via read.groups(). `order` sets
 * the bucket's position in the instance array.
 */
export const AgentPageContextGroup = ({
  groupKey,
  order = 0,
  children,
}: {
  groupKey: string;
  order?: number;
  children?: ReactNode;
}) => {
  const scope = useMemo<GroupScope>(
    () => ({groupKey, order}),
    [groupKey, order]
  );

  return (
    <GroupContext.Provider value={scope}>{children}</GroupContext.Provider>
  );
};

/**
 * usePublishAgentPageContext publishes `value` to `channel` for the component's
 * mount lifetime, binding it to the nearest enclosing layer and group. A null
 * value publishes nothing.
 */
export function usePublishAgentPageContext<T>(
  channel: AgentPageContextChannel<T>,
  value: T | null
): void {
  const layer = useContext(LayerContext);
  const group = useContext(GroupContext);

  // NOTE: Sync the value into a ref from an effect, not during render, so a
  // discarded concurrent render cannot leave an uncommitted value to be read
  // at send time.
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  });

  const groupKey = group?.groupKey ?? null;
  const order = group?.order ?? 0;
  useEffect(
    () =>
      registerAgentPageContextFact({
        channel: channel as AgentPageContextChannel<unknown>,
        layer,
        groupKey,
        order,
        get: () => valueRef.current,
      }),
    [channel, layer, groupKey, order]
  );
}

// useStableLayer mints one layer per mount so its identity is stable across
// StrictMode's double invocation.
function useStableLayer(
  type: string,
  parent: AgentPageContextLayerNode | null
): AgentPageContextLayerNode {
  const ref = useRef<AgentPageContextLayerNode>(undefined);
  if (ref.current === undefined) {
    ref.current = {type, depth: parent == null ? 0 : parent.depth + 1};
  }

  // oxlint-disable-next-line react/refs
  return ref.current;
}
